import React, { useEffect, useState } from "react";
import { Empty, Spin } from "antd";
import { renderInlineMarkdown } from "../BIMFramework/markdownInline.js";
import "./Roadmap.css";

// A status board, not a wiki page — one hand-edited pages/roadmap/roadmap.md ("## Column" +
// "- [Area] Item" bullets), not a scanned folder tree like Team/BIM Framework/Learn/Softwares.
// Columns are whatever headings the .md actually has, in file order (server/routes/roadmap.js),
// so this component has no hardcoded "Planned/In Progress/Done" list either — COLUMN_STYLE below
// is a curated look for the three expected ones, with a generic fallback for anything else, same
// "curated + graceful fallback" shape as every ICONS lookup elsewhere in this app.
const COLUMN_STYLE = {
  "done":         { accent: "var(--success)" },
  "in progress":  { accent: "var(--accent)" },
  "planned":      { accent: "var(--warning)" },
  "needs":        { accent: "var(--danger)" },
};
const styleFor = (title) => COLUMN_STYLE[title.trim().toLowerCase()] || { accent: "var(--border-light)" };

// Clusters a column's cards by their [Area] tag instead of just listing them in file order —
// as the board grows past a handful of items, a boss scanning the page should see "Digital
// Twin work" and "BIM Management work" as distinct clusters, not have to read every card's tag
// individually to notice the breadth of what's covered. Preserves first-seen order for both
// groups and items within a group (matches the file's own order, not alphabetical) — untagged
// items form their own trailing group with no sub-header, since there's nothing to label it with.
function groupByArea(items) {
  const groups = [];
  const byArea = new Map();
  const ungrouped = [];
  for (const item of items) {
    if (!item.area) { ungrouped.push(item); continue; }
    if (!byArea.has(item.area)) {
      const group = { area: item.area, items: [] };
      byArea.set(item.area, group);
      groups.push(group);
    }
    byArea.get(item.area).items.push(item);
  }
  if (ungrouped.length > 0) groups.push({ area: null, items: ungrouped });
  return groups;
}

export default function Roadmap() {
  const [columns, setColumns] = useState(null);

  useEffect(() => {
    fetch("/api/roadmap")
      .then((r) => r.json())
      .then((d) => setColumns(d.columns || []))
      .catch(() => setColumns([]));
  }, []);

  return (
    <div className="roadmap-page">
      <div className="roadmap-hero">
        <h1 className="roadmap-hero-title">
          Design Technology <em>Roadmap</em>
        </h1>
      </div>

      {columns === null ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : columns.length === 0 ? (
        <Empty description="Nothing planned yet" style={{ marginTop: 40 }} />
      ) : (
        <div className="roadmap-board">
          {columns.map((col) => {
            const { accent } = styleFor(col.title);
            return (
              <div key={col.title} className="roadmap-column" style={{ "--col-accent": accent }}>
                <div className="roadmap-column-title">
                  {col.title}
                  <span className="roadmap-column-count">{col.items.length}</span>
                </div>
                {col.items.length === 0 ? (
                  <div className="roadmap-column-empty">Nothing here yet</div>
                ) : (
                  groupByArea(col.items).map((group, gi) => (
                    <div key={group.area || `_ungrouped_${gi}`} className="roadmap-group">
                      {group.area && <div className="roadmap-group-label">{group.area}</div>}
                      {/* Numbers disabled per user request (kept, not deleted, in case they
                          come back later) — was "restarts at 1 for every group" via i + 1:
                          <div className="roadmap-card-number">{i + 1}</div> */}
                      {group.items.map((item, i) => (
                        <div key={i} className="roadmap-card">
                          {item.sub && <div className="roadmap-card-sub">{item.sub}</div>}
                          <div className="roadmap-card-text">{renderInlineMarkdown(item.text)}</div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
